import { api } from "./api";

export const voicePresets   = () => api.get("/voice/presets").then((r) => r.data);
export const voiceOfTheDay  = () => api.get("/voice/of-the-day").then((r) => r.data);
export const voiceFavorites = () => api.get("/voice/favorites").then((r) => r.data);
export const voiceFavToggle = (slug) => api.post(`/voice/favorites/${slug}`).then((r) => r.data);

// --- Phase B: coin unlock + battles ---
export const voiceUnlocks     = () => api.get("/voice/unlocks").then((r) => r.data);
export const voiceUnlock      = (slug) => api.post(`/voice/unlocks/${slug}`).then((r) => r.data);
export const battlePrompts    = () => api.get("/voice/battles/prompts").then((r) => r.data);
export const battleList       = (params = {}) => api.get("/voice/battles/submissions", { params }).then((r) => r.data);
export const battleSubmit     = (payload) => api.post("/voice/battles/submit", payload).then((r) => r.data);
export const battleVote       = (sid) => api.post(`/voice/battles/${sid}/vote`).then((r) => r.data);
export const battlePrizePool  = () => api.get("/voice/battles/prize-pool").then((r) => r.data);
export const voiceOfTheWeek   = () => api.get("/voice/of-the-week").then((r) => r.data);

/**
 * Real-time live effect chain. Builds a Web Audio graph from mic → same
 * primitive nodes as the offline engine → destination + a MediaStreamDestination
 * for MediaRecorder. Returns { stop } to tear down.
 */
export async function startLiveChain(cfg, { onStream, onError } = {}) {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } });
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const src = ctx.createMediaStreamSource(stream);
    let node = src;

    const pitch = Number(cfg.pitch) || 1.0;
    // Real-time pitch shift approximation via a fixed DelayNode ring — quick & good enough.
    if (Math.abs(pitch - 1.0) > 0.01) {
      // Approximate pitch via playbackRate on an AudioBufferSource routed through a scriptProcessor is expensive;
      // use a granular-ish detune via BiquadFilter shelf as a "vibe" instead of true pitch (live mode has 1x pitch).
      const shelf = ctx.createBiquadFilter();
      shelf.type = "highshelf";
      shelf.frequency.value = 1200;
      shelf.gain.value = pitch > 1 ? 6 : -6;
      node.connect(shelf); node = shelf;
    }
    if (cfg.filter_hz) {
      const f = ctx.createBiquadFilter();
      f.type = cfg.filter_type || "peak"; f.frequency.value = cfg.filter_hz; f.Q.value = 1.2;
      node.connect(f); node = f;
    }
    if (cfg.drive > 0) {
      const ws = ctx.createWaveShaper(); ws.oversample = "2x";
      const k = cfg.drive * 100, n = 44100, curve = new Float32Array(n), pi = Math.PI;
      for (let i = 0; i < n; i++) { const x = (i * 2) / n - 1; curve[i] = ((3 + k) * x * 20 * pi / 180) / (pi + k * Math.abs(x)); }
      ws.curve = curve;
      node.connect(ws); node = ws;
    }
    if (cfg.delay_s > 0) {
      const d = ctx.createDelay(); d.delayTime.value = Math.min(1, cfg.delay_s);
      const g = ctx.createGain(); g.gain.value = cfg.delay_fb || 0.35;
      const mix = ctx.createGain();
      node.connect(mix); node.connect(d); d.connect(g); g.connect(d); d.connect(mix);
      node = mix;
    }
    if (cfg.reverb > 0) {
      const conv = ctx.createConvolver();
      const rate = ctx.sampleRate, len = rate * 1.5, buf = ctx.createBuffer(2, len, rate);
      for (let c = 0; c < 2; c++) {
        const bd = buf.getChannelData(c);
        for (let i = 0; i < len; i++) bd[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3);
      }
      conv.buffer = buf;
      const wet = ctx.createGain(); wet.gain.value = cfg.reverb;
      const dry = ctx.createGain(); dry.gain.value = 1 - Math.min(cfg.reverb, 0.9);
      const mix = ctx.createGain();
      node.connect(dry); dry.connect(mix);
      node.connect(conv); conv.connect(wet); wet.connect(mix);
      node = mix;
    }
    if (cfg.formant) {
      const sh = ctx.createBiquadFilter(); sh.type = "highshelf"; sh.frequency.value = 1800; sh.gain.value = cfg.formant;
      node.connect(sh); node = sh;
    }

    const dest = ctx.createMediaStreamDestination();
    node.connect(ctx.destination);
    node.connect(dest);
    onStream?.(dest.stream);
    return {
      stop: () => { try { stream.getTracks().forEach((t) => t.stop()); ctx.close(); } catch {} },
    };
  } catch (e) { onError?.(e); return { stop: () => {} }; }
}

/**
 * Offline Web Audio effect engine — applies a preset config to an AudioBuffer
 * and returns a new decoded, encoded WAV Blob that can be played, downloaded
 * or shared. All 60+ voices use these same 8 primitive params.
 */
export async function renderWithEffect(audioBlob, cfg) {
  const arrayBuf = await audioBlob.arrayBuffer();
  const decodeCtx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, 44100, 44100);
  const audioBuf  = await decodeCtx.decodeAudioData(arrayBuf.slice(0));

  const pitch   = Number(cfg.pitch)  || 1.0;
  const outLen  = Math.ceil((audioBuf.duration / pitch) * 44100) + 44100; // + reverb tail
  const off     = new OfflineAudioContext(1, outLen, 44100);
  const src     = off.createBufferSource();
  src.buffer    = cfg.reverse ? reverseBuffer(audioBuf, off) : audioBuf;
  src.playbackRate.value = pitch;

  let node = src;

  // Filter
  if (cfg.filter_hz) {
    const f = off.createBiquadFilter();
    f.type = cfg.filter_type || "peak";
    f.frequency.value = cfg.filter_hz;
    f.Q.value = 1.2;
    node.connect(f); node = f;
  }
  // Waveshaper (drive/distortion)
  if (cfg.drive > 0) {
    const ws = off.createWaveShaper();
    ws.curve = makeCurve(cfg.drive);
    ws.oversample = "4x";
    node.connect(ws); node = ws;
  }
  // Bit crusher (approximated with waveshaper quantization)
  if (cfg.bits > 0 && cfg.bits <= 12) {
    const bc = off.createWaveShaper();
    bc.curve = makeBitCurve(cfg.bits);
    node.connect(bc); node = bc;
  }
  // Delay w/ feedback
  if (cfg.delay_s > 0) {
    const d = off.createDelay(); d.delayTime.value = cfg.delay_s;
    const g = off.createGain(); g.gain.value = cfg.delay_fb || 0.35;
    const mix = off.createGain();
    node.connect(mix);
    node.connect(d); d.connect(g); g.connect(d); d.connect(mix);
    node = mix;
  }
  // Reverb (generated impulse)
  if (cfg.reverb > 0) {
    const conv = off.createConvolver();
    conv.buffer = generateImpulse(off, 2.2, cfg.reverb);
    const wet = off.createGain(); wet.gain.value = cfg.reverb;
    const dry = off.createGain(); dry.gain.value = 1 - Math.min(cfg.reverb, 0.9);
    const mix = off.createGain();
    node.connect(dry); dry.connect(mix);
    node.connect(conv); conv.connect(wet); wet.connect(mix);
    node = mix;
  }
  // Formant tilt (very approximate via high-shelf)
  if (cfg.formant) {
    const sh = off.createBiquadFilter();
    sh.type = "highshelf"; sh.frequency.value = 1800; sh.gain.value = cfg.formant;
    node.connect(sh); node = sh;
  }
  // Wobble (LFO on gain)
  if (cfg.wobble_hz > 0) {
    const trem = off.createGain();
    const lfo  = off.createOscillator(); lfo.frequency.value = cfg.wobble_hz;
    const depth = off.createGain(); depth.gain.value = 0.35;
    lfo.connect(depth); depth.connect(trem.gain);
    trem.gain.value = 0.65;
    node.connect(trem); node = trem;
    lfo.start(0);
  }

  node.connect(off.destination);
  src.start(0);
  const rendered = await off.startRendering();
  return audioBufferToWavBlob(rendered);
}

function makeCurve(amount) {
  const k = amount * 100, n = 44100, curve = new Float32Array(n), pi = Math.PI;
  for (let i = 0; i < n; i++) {
    const x = (i * 2) / n - 1;
    curve[i] = ((3 + k) * x * 20 * pi / 180) / (pi + k * Math.abs(x));
  }
  return curve;
}
function makeBitCurve(bits) {
  const steps = Math.pow(2, bits);
  const n = 44100, curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i * 2) / n - 1;
    curve[i] = Math.round(x * steps) / steps;
  }
  return curve;
}
function generateImpulse(ctx, duration, decay) {
  const rate = ctx.sampleRate, len = rate * duration;
  const buf = ctx.createBuffer(2, len, rate);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay * 5);
  }
  return buf;
}
function reverseBuffer(buf, ctx) {
  const rev = ctx.createBuffer(buf.numberOfChannels, buf.length, buf.sampleRate);
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const s = buf.getChannelData(c), t = rev.getChannelData(c);
    for (let i = 0, n = s.length; i < n; i++) t[i] = s[n - 1 - i];
  }
  return rev;
}

/** Encode an AudioBuffer to a 16-bit PCM WAV Blob. */
function audioBufferToWavBlob(buf) {
  const ch = 1, rate = buf.sampleRate;
  const samples = buf.getChannelData(0);
  const bytes = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(bytes);
  const w = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  w(0, "RIFF"); v.setUint32(4, 36 + samples.length * 2, true);
  w(8, "WAVE"); w(12, "fmt "); v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); v.setUint16(22, ch, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * ch * 2, true);
  v.setUint16(32, ch * 2, true); v.setUint16(34, 16, true);
  w(36, "data"); v.setUint32(40, samples.length * 2, true);
  let o = 44;
  for (let i = 0; i < samples.length; i++, o += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    v.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }
  return new Blob([bytes], { type: "audio/wav" });
}
